import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Download, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { CalendarPopover } from '@/components/calendar-popover'
import { OrgUserInvoicesTableProps } from '../types'
import { DataTable, TableColumn, SortableHeader } from '@/components/data-table'
import { ErrorMessage } from '@/components/error-message'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useGetOrgUserInvoices } from '@/services/billing'
import { DateRange } from 'react-day-picker'
import Gravatar from 'react-gravatar'
import { getFormattedUnixDate } from '@/helpers/time'
import { Invoice } from '@/services/inference.gen'

dayjs.extend(utc)

// Define a type for the transformed invoice data
type TransformedInvoice = Invoice & {
  formattedAmount: string
  formattedDate: string
  invoiceTypeLabel: string
}

export const OrgUserInvoicesTable = ({ userId }: OrgUserInvoicesTableProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().subtract(6, 'day').startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  })
  const [sortBy, setSortBy] = useState<string>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useGetOrgUserInvoices(
    userId,
    {
      start_at: getFormattedUnixDate(dateRange.from, { time: 'startOfDay' }),
      end_at: getFormattedUnixDate(dateRange.to, { time: 'endOfDay' }),
      sort_by: sortBy,
      sort_direction: sortDirection,
      search: searchTerm,
    },
    10
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(columnId)
      setSortDirection('desc')
    }
  }

  const invoices = data?.pages.flatMap((page) => page.items) || []

  const columns: TableColumn<TransformedInvoice>[] = [
    {
      id: 'stripe_invoice_id',
      sortable: true,
      width: 'w-[18%]',
      header: ({ isSorted, sortDirection }) => (
        <SortableHeader
          label="Invoice ID"
          isSorted={isSorted}
          sortDirection={sortDirection}
          className="font-semibold"
        />
      ),
      cell: (invoice) => (
        <div className="font-medium">{invoice.stripe_invoice_id}</div>
      ),
    },
    {
      id: 'created_at',
      sortable: true,
      width: 'w-[15%]',
      header: ({ isSorted, sortDirection }) => (
        <SortableHeader
          label="Date"
          isSorted={isSorted}
          sortDirection={sortDirection}
          className="font-semibold"
        />
      ),
      cell: (invoice) => invoice.formattedDate,
    },
    {
      id: 'amount',
      width: 'w-[15%]',
      header: 'Amount',
      cell: (invoice) => invoice.formattedAmount,
    },
    {
      id: 'user',
      width: 'w-[25%]',
      header: 'User',
      cell: (invoice) => {
        const userProfile = invoice.user_organization?.profile_metadata
        const userEmail = invoice.user?.email || 'N/A'

        // Check if profile exists and has necessary data before rendering
        const hasProfileData =
          userProfile && userProfile.first_name && userProfile.last_name

        return (
          <div className="flex items-center gap-2">
            {hasProfileData && userProfile.avatar_preview_url ? (
              <img
                src={userProfile.avatar_preview_url}
                alt={`${userProfile.first_name} ${userProfile.last_name}`}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <Gravatar email={userEmail} className="w-8 h-8 rounded-full" />
            )}
            <div>
              {hasProfileData ? (
                <p>{`${userProfile.first_name} ${userProfile.last_name}`}</p>
              ) : (
                <p>N/A</p> // Show N/A if no profile data
              )}
              <p className="text-sm text-muted-foreground">{userEmail}</p>
            </div>
          </div>
        )
      },
    },
    {
      id: 'type',
      width: 'w-[10%]',
      header: 'Type',
      cell: (invoice) => invoice.invoiceTypeLabel,
    },
    {
      id: 'actions',
      width: 'w-[12%]',
      header: '',
      cell: (invoice) => {
        const downloadUrl = invoice.metadata?.download_page || ''
        return (
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 disabled:opacity-50"
            disabled={!downloadUrl}
          >
            <a
              href={downloadUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
          </Button>
        )
      },
    },
  ]

  const originalHeader = (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4 flex-grow">
          <h2 className="text-xl font-semibold">User Invoices</h2>
          <div className="relative flex-grow">
            <Search className="absolute w-4 h-4 left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search Invoice ID, Date, or Amount..."
              className="pl-10 w-full"
              value={searchTerm}
              onChange={handleSearchChange}
              maxLength={100}
            />
          </div>
        </div>
        <CalendarPopover
          range={dateRange}
          onSelect={(range) => {
            if (range?.from && range?.to) {
              setDateRange({ from: range.from, to: range.to })
            }
          }}
        />
      </div>
    </div>
  )

  if (isError) {
    return (
      <div className="flex justify-center items-center h-64">
        <ErrorMessage
          message={`Error loading invoices: ${error instanceof Error ? error.message : String(error)}`}
        />
      </div>
    )
  }

  return (
    <DataTable
      customHeader={originalHeader}
      data={invoices as TransformedInvoice[]}
      columns={columns}
      keyExtractor={(invoice) => invoice.stripe_invoice_id}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      emptyStateMessage="No invoices available"
      endStateMessage="End of invoices list"
      currentSort={{ id: sortBy, direction: sortDirection }}
      onSortChange={handleSort}
      loadingMessage="Loading user invoices..."
    />
  )
}
